import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { message } from 'antd';
import { useGetPublicAccountQuery } from '../../store/api/metricsApi';
import Button from '../../reusable/Button/Button';
import DataTable from '../../reusable/DataTable/DataTable';
import StatusChip from '../../reusable/StatusChip/StatusChip';
import SymbolFlag from '../../reusable/SymbolFlag/SymbolFlag';
import CertificateArt from '../PayoutCertificates/Certificates/CertificateArt';
import { usd } from '../../utilities/format';
import { t } from '../../i18n/i18n';
import { toShareView } from './data';
import './PublicProfile.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

const toneOf = (value) => (String(value).trim().startsWith('-') ? 'down' : 'up');

const PAYOUT_COLUMNS = [
  { key: 'requested', header: 'Requested on', width: 34 },
  { key: 'amount', header: 'Amount', width: 26 },
  {
    key: 'status',
    header: 'Status',
    width: 40,
    render: (row) => <StatusChip>{row.status}</StatusChip>,
  },
];

export default function PublicProfile() {
  const { accountId } = useParams();
  // The link carries the platform the login belongs to; MT5 is the default the
  // endpoint is queried with when a shared link was built without one.
  const [search] = useSearchParams();
  const { data, isLoading, isError } = useGetPublicAccountQuery({
    accountId,
    platform: search.get('platform') || 'mt5',
  });

  const {
    snapshotDate,
    accountName,
    accountMeta,
    headline,
    certificates,
    statistics,
    trades,
    payouts,
  } = useMemo(() => toShareView(data, t), [data]);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (e) => e.key === 'Escape' && setPreview(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview]);

  // A wrong login, the wrong platform on the link, or an account whose holder
  // has sharing off — the endpoint answers 404 to all three.
  if (isError || (!isLoading && !data)) {
    return (
      <div className="public-profile">
        <div className="pp-state">
          <h1 className="pp-state__title">{t('This page isn’t available')}</h1>
          <p className="pp-state__text">
            {t('The link may be wrong, or the account holder may have turned sharing off.')}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="public-profile">
        <div className="pp-state">
          <p className="pp-state__text">{t('Loading account…')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-profile">
      <header className="pp-top">
        <span className="icon pp-top__brand" style={icon('brand-alphacapital')} />
        <span className="pp-verified">
          <span className="pp-verified__seal">
            <span className="icon" style={icon('brand-mark')} />
          </span>
          {t('Verified by Alpha Capital')}
        </span>

        <div className="pp-top__right">
          <span className="pp-top__snapshot">{t('Snapshot')} · {snapshotDate}</span>
          <CopyToClipboard
            text={window.location.href}
            onCopy={(_text, ok) =>
              ok ? message.success(t('Link copied')) : message.error(t('Could not copy link'))
            }
          >
            <Button variant="toolbar" className="pp-copy">
              <span className="icon pp-copy__icon" style={icon('copy')} />
              {t('Copy link')}
            </Button>
          </CopyToClipboard>
          <a className="pp-cta" href="/sign-up">
            {t('Start a challenge')}
            <span className="icon pp-cta__arrow" style={icon('arrow-up-right')} />
          </a>
        </div>
      </header>

      <main className="pp-main">
        <section className="pp-hero">
          <div className="pp-hero__profile">
            <span className="pp-hero__avatar">
              <span className="icon pp-hero__mark" style={icon('brand-mark')} />
            </span>
            <div className="pp-hero__foot">
              <div className="pp-hero__id">
                <span className="pp-hero__name">{accountName}</span>
                <span className="pp-hero__meta">{accountMeta}</span>
              </div>
              <span className="pp-readonly">{t('Read-only')}</span>
            </div>
          </div>

          {headline.map((stat) => (
            <div className="pp-stat" key={stat.label}>
              <div className="pp-stat__head">
                <span className="icon pp-stat__icon" style={icon(stat.icon)} />
                <span className="pp-stat__label">{t(stat.label)}</span>
              </div>
              <div className="pp-stat__body">
                <div className="pp-stat__value">
                  <span className="pp-stat__number">{stat.value}</span>
                  {stat.unit && <span className="pp-stat__unit">{stat.unit}</span>}
                </div>
                <p className="pp-stat__note">
                  {stat.note}
                  {stat.accent && (
                    <span className={`is-${stat.accentTone}`}>{stat.accent}</span>
                  )}
                  {stat.noteEnd}
                </p>
              </div>
            </div>
          ))}
        </section>

        {/* An account with no cleared payout has no certificates to march. */}
        {certificates.length > 0 && (
        <section className="pp-section">
          <div className="pp-sectionhead">
            <span className="pp-sectionhead__title">{t('Payout certificates')}</span>
            <span className="pp-sectionhead__note">
              {t('Verified payouts on this account. Hover for colour · click to enlarge.')}
            </span>
          </div>

          <div className="pp-certs">
            {/* The set is rendered twice so the marquee loops on a -50% shift. */}
            <div className="pp-certs__track">
              {[0, 1].map((copy) => (
                <div className="pp-certs__set" key={copy} aria-hidden={copy === 1}>
                  {certificates.map((item) => (
                    <button
                      type="button"
                      className="pp-cert"
                      key={`${copy}-${item.id}`}
                      onClick={() => setPreview(item)}
                    >
                      <CertificateArt item={item} className="pp-cert__art" loading="lazy" />
                      <span className="pp-cert__meta">
                        <span>{usd(item.amount, { decimals: 2 })}</span>
                        <span>{item.title}</span>
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
        )}

        <div className="pp-split">
          <section className="pp-card">
            <header className="pp-card__head">
              <span className="pp-card__title">
                <span className="icon pp-card__icon" style={icon('chart-profit')} />
                {t('Statistics')}
              </span>
              <span className="pp-muted">{statistics.note}</span>
            </header>

            <div className="pp-figures">
              {statistics.stats.map((stat) => (
                <div className="pp-figure" key={stat.label}>
                  <span className="pp-muted">{t(stat.label)}</span>
                  <span className={`pp-figure__value${stat.tone ? ` is-${stat.tone}` : ''}`}>
                    {stat.value}
                  </span>
                </div>
              ))}
            </div>

            <span className="pp-rule" />

            <header className="pp-card__head">
              <span className="pp-muted">{t('Volume by symbol')}</span>
              <span className="pp-muted">{t('Share of closed trades')}</span>
            </header>

            <div className="pp-symbols">
              {statistics.symbols.map((row) => (
                <div className="pp-symbol" key={row.symbol}>
                  <span className="pp-symbol__name">
                    <SymbolFlag symbol={row.symbol} />
                    {row.symbol}
                  </span>
                  <span className="pp-bar">
                    <span className="pp-bar__fill" style={{ width: `${row.pct}%` }} />
                  </span>
                  <span className="pp-symbol__pct">{row.pct}%</span>
                </div>
              ))}
            </div>
          </section>

          <section className="pp-card">
            <header className="pp-card__head">
              <span className="pp-card__title">
                <span className="icon pp-card__icon" style={icon('nav-trades')} />
                {t('Recent trades')}
              </span>
              <span className="pp-muted">{t('Closed positions')}</span>
            </header>

            <div className="pp-trades">
              {trades.rows.map((row) => (
                <div className="pp-trade" key={row.id}>
                  <span className="pp-trade__copy">
                    <span className="pp-trade__sym">
                      <SymbolFlag symbol={row.symbol} size={20} />
                      {row.symbol}
                    </span>
                    <span className="pp-muted">{row.detail}</span>
                  </span>
                  <span className="pp-trade__num">
                    <span className={`pp-trade__pnl is-${toneOf(row.pnl)}`}>{row.pnl}</span>
                    <span className="pp-muted">{row.meta}</span>
                  </span>
                </div>
              ))}
            </div>

            <footer className="pp-trades__foot">
              <span className="pp-muted">{trades.shown}</span>
              <span className={`is-${trades.netTone}`}>{trades.net}</span>
            </footer>
          </section>
        </div>

        <section className="pp-section">
          <div className="pp-sectionhead">
            <span className="pp-sectionhead__title">{t('Payouts')}</span>
            <span className="pp-sectionhead__note">
              {t('Lifetime totals, then the three largest cleared withdrawals.')}
            </span>
          </div>

          <div className="pp-pulse">
            {payouts.pulse.map((item) => (
              <div className="pp-pulse__item" key={item.label}>
                <span className="pp-pulse__label">{t(item.label)}</span>
                <span className="pp-pulse__value">{item.value}</span>
              </div>
            ))}
          </div>

          {payouts.top.length > 0 && (
            <DataTable
              title="Top payouts"
              icon="nav-payout"
              columns={PAYOUT_COLUMNS}
              rows={payouts.top}
              minWidth={448}
              pagination={false}
            />
          )}
        </section>

        <footer className="pp-footer">
          <span>
            {t(
              'Shared by the account holder as a read-only snapshot. Email, login credentials, payment details and reference numbers are never part of a public performance page.'
            )}
          </span>
          <span className="pp-footer__fine">
            {t('Past performance is not indicative of future results. Trading involves risk of loss.')}
          </span>
        </footer>
      </main>

      {preview && (
        <div className="pp-modal" role="presentation" onClick={() => setPreview(null)}>
          <div className="pp-modal__panel" role="presentation" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="pp-modal__close"
              aria-label={t('Close certificate')}
              onClick={() => setPreview(null)}
            >
              ×
            </button>
            <CertificateArt item={preview} className="pp-modal__art" />
            <div className="pp-modal__meta">
              <span>{usd(preview.amount, { decimals: 2 })}</span>
              <span>{preview.title}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
